// src/components/profile/HeaderOverview.jsx
import React, { useState, useEffect } from 'react';
import { Badge as UIBadge } from '../ui/badge';
import { Clock, Trophy, FolderOpen, Info, DollarSign, Target, Zap, X } from 'lucide-react';
import { selectHeader, selectROI } from '../../adapters/profile/selectors';
import { fmtInt, fmtRank } from '../../utils/format';
import { labelLens } from '../../utils/rankLens';
import { Provenance } from '../../utils/provenance';
import { SHOW_METRICS_IN_HEADER } from '../../constants/uiFlags';

const CompactGauge = ({ value = 0, label, color = "green", showProvenance = false, source = "", updatedAt = null }) => {
  const getScoreColor = (score) => {
    if (score >= 70) return "text-green-600";
    if (score >= 40) return "text-yellow-600";
    return "text-red-600";
  };
  
  const scoreColor = getScoreColor(value);
  const colorMap = {
    green: scoreColor,
    blue: scoreColor, 
    orange: scoreColor
  };
  
  return (
    <div className="flex flex-col items-center min-w-[60px]">
      <div className="relative w-12 h-12">
        <svg className="transform -rotate-90 w-full h-full" viewBox="0 0 36 36">
          <path className="text-gray-200" strokeWidth="3" stroke="currentColor" fill="none" d="M18 2a16 16 0 1 1 0 32 16 16 0 0 1 0-32" />
          <path className={`${colorMap[color]} transition-all duration-500`} strokeWidth="3" strokeLinecap="round" stroke="currentColor" fill="none" strokeDasharray={`${value}, 100`} d="M18 2a16 16 0 1 1 0 32 16 16 0 0 1 0-32" />
        </svg>
        <div className={`absolute inset-0 flex items-center justify-center text-sm font-bold ${colorMap[color]}`}>
          {Math.round(value)}
        </div>
      </div>
      <div className="text-xs text-gray-600 mt-1 text-center flex items-center">
        {label}
        {showProvenance && <Provenance source={source} updatedAt={updatedAt} />}
      </div>
    </div>
  );
};

export default function HeaderOverview({ 
  profile, 
  scores,
  roiData,
  roiMetrics,  // Calculated metrics from useRoiData hook
  lens = '70_30',
  displayMode = 'raw'
}) {
  const [isSticky, setIsSticky] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsSticky(scrollTop > 100);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prepare API payload for selector
  const apiPayload = {
    use_case: profile,
    scores: scores || {
      value_score: profile?.value_score,
      implementation_score: profile?.implementation_score,
      total_score: profile?.total_score,
      rank: profile?.rank,
      updated_at: profile?.updated_at
    },
    ...profile,
    ...scores
  };

  // Use selector to get header data with RAW scores
  const h = selectHeader(apiPayload, { lens, displayMode });

  // Extract display values
  const title = h.name || profile?.title || 'Use Case';
  
  // Map category_id to category name
  const categoryId = profile?.category_id || profile?.categoryId;
  const categoryMap = {
    1: 'Healthcare Service Delivery',
    2: 'Research & Development',
    3: 'Patient Experience',
    4: 'Provider Solutions',
    5: 'Value-Based Care',
    6: 'Operational Excellence',
    7: 'Digital Health Products'
  };
  
  // Get category name from the map or use the category field if it exists
  const category = profile?.category || 
                   profile?.category_name || 
                   profile?.categoryName ||
                   categoryMap[categoryId] ||
                   'Analytics';
  
  // OPTION A: Use ROI metrics from hook if available
  let roiValue = null;
  let paybackValue = null;
  let roiRange = '0-0%';
  let timeToRoi = '6-12 months';
  
  // Check for metrics from useRoiData hook first
  if (SHOW_METRICS_IN_HEADER && roiMetrics) {
    roiValue = roiMetrics.roiPct;
    paybackValue = roiMetrics.paybackMonths;
    
    // For header, display the actual calculated ROI
    if (roiValue !== null && roiValue !== undefined && !isNaN(roiValue)) {
      // Handle negative ROI properly
      if (roiValue < 0) {
        // For negative ROI, show absolute value with negative sign
        roiRange = `${Math.round(roiValue)}%`;
      } else {
        // For positive ROI, show as is
        roiRange = `${Math.round(roiValue)}%`;
      }
      
      // Fallback to default range if calculation seems wrong
      if (roiValue < -100 || roiValue > 1000) {
        roiRange = profile?.roi_range || '150-200%';
      }
    }
    
    if (paybackValue !== null && paybackValue !== undefined) {
      if (!paybackValue || paybackValue <= 0) {
        timeToRoi = 'No payback';
      } else if (paybackValue <= 12) {
        timeToRoi = `${Math.round(paybackValue)} months`;
      } else {
        // Convert to years if > 12 months
        const years = Math.floor(paybackValue / 12);
        const months = Math.round(paybackValue % 12);
        if (months === 0) {
          timeToRoi = `${years} year${years > 1 ? 's' : ''}`;
        } else {
          timeToRoi = `${years} year${years > 1 ? 's' : ''}, ${months} month${months > 1 ? 's' : ''}`;
        }
      }
    }
  }
  
  // Fallback to profile/roiData values if no metrics from hook
  if (!SHOW_METRICS_IN_HEADER || !roiMetrics || roiRange === '0-0%') {
    // Try to use selector
    const roiModel = selectROI(
      roiData?.assumptions || [],
      roiData?.calculations || []
    );
    
    if (roiModel.roiPercentage !== null && roiModel.roiPercentage !== undefined) {
      const roiVal = Math.round(roiModel.roiPercentage);
      // Create range for fallback
      const lower = Math.max(0, roiVal - 10);
      const upper = roiVal + 10;
      roiRange = `${lower}-${upper}%`;
    }
    
    if (roiModel.paybackMonths !== null && roiModel.paybackMonths !== undefined) {
      const months = roiModel.paybackMonths;
      if (months <= 6) timeToRoi = '3-6 months';
      else if (months <= 12) timeToRoi = '6-12 months';
      else if (months <= 18) timeToRoi = '12-18 months';
      else if (months <= 24) timeToRoi = '18-24 months';
      else timeToRoi = '24+ months';
    }
    
    // Final fallback to profile values
    roiRange = roiRange === '0-0%' ? (profile?.roi_range || '150-200%') : roiRange;
    timeToRoi = timeToRoi === '6-12 months' ? (profile?.time_to_roi || '6-12 months') : timeToRoi;
  }
  
  const priority = profile?.priority_label || 
    (h.global7030Rank <= 10 ? 'High' : 
      h.global7030Rank <= 30 ? 'Medium' : 'Low');

  // Use RAW scores from selector
  const value = scores?.value ?? scores?.value_score ?? profile?.value_score ?? 0;
  const ease = scores?.ease ?? scores?.implementation_score ?? profile?.implementation_score ?? 0;
  const total = scores?.total ?? scores?.total_score ?? profile?.total_score ?? 0;
  const globalRank = scores?.rank ?? h.global7030Rank ?? 1;
  const lensRank = h.lensRank;

  return (
    <>
      {/* Main Header */}
      <header className="bg-white border-b border-gray-200 relative" aria-labelledby="overview-title">
        {/* Close Button */}
        <button
          onClick={() => window.history.back()}
          className="absolute top-6 right-6 p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 hover:text-gray-900 z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-3 gap-4 items-center">
            
            {/* Left Section (1/3): Title + Category */}
            <div className="flex flex-col gap-3">
              <h1 id="overview-title" className="text-[26px] font-bold text-[#18365E] tracking-tight leading-tight break-words">
                {title}
              </h1>
              <UIBadge className="bg-[#2C5282] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 w-fit">
                <FolderOpen className="w-4 h-4" />
                {category}
              </UIBadge>
            </div>

            {/* Center Section (1/3): ROI and Badges - OPTION A DISPLAY */}
            <div className="flex flex-col items-center gap-3">
              {/* ROI Display - Single Authoritative Location */}
              <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-500 rounded-xl px-4 py-3 shadow-lg w-full max-w-[240px]">
                <div className="text-center">
                  <div className="text-xs uppercase tracking-wide text-green-700 font-semibold flex items-center justify-center gap-1">
                    EXPECTED ROI
                    {roiMetrics?.source && (
                      <Provenance 
                        source={`uc_roi_${roiMetrics.source}.result`}
                        updatedAt={roiMetrics.updatedAt}
                      />
                    )}
                  </div>
                  <div className="text-xl font-bold text-green-600">
                    {roiRange}
                  </div>
                  <div className="text-sm text-gray-600">
                    {timeToRoi === 'No payback' ? (
                      <span className="text-red-600">No payback</span>
                    ) : (
                      <>in {timeToRoi}</>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Priority and Rank Badges */}
              <div className="flex gap-2">
                <UIBadge className="bg-[#2C5282] text-white px-3 py-1.5 rounded text-xs font-bold shadow-sm">
                  {priority} Priority
                </UIBadge>
                <UIBadge className="bg-[#D69E2E] text-white px-3 py-1.5 rounded text-xs font-bold shadow-sm">
                  Rank #{globalRank}
                </UIBadge>
              </div>
            </div>

            {/* Right Section (1/3): Scores */}
            <div className="flex justify-end">
              <div className="flex flex-col items-center gap-2">
                {/* Score Gauges on Top */}
                <div className="flex items-center gap-4">
                  <CompactGauge 
                    value={value} 
                    label="Value" 
                    color="green"
                    showProvenance={true}
                    source="client_use_case_scores.value_score"
                    updatedAt={h.updatedAt}
                  />
                  <CompactGauge 
                    value={ease} 
                    label="Ease" 
                    color="blue"
                    showProvenance={true}
                    source="client_use_case_scores.implementation_score"
                    updatedAt={h.updatedAt}
                  />
                  <CompactGauge 
                    value={total} 
                    label="Total" 
                    color="orange"
                    showProvenance={true}
                    source="client_use_case_scores.total_score"
                    updatedAt={h.updatedAt}
                  />
                </div>
                
                {/* Score/100 and Stars on Bottom */}
                <div className="flex items-center gap-4">
                  <div className="text-2xl font-bold text-gray-700">
                    {Math.round(total)}/100
                    {displayMode === 'calibrated' && (
                      <span className="text-xs text-gray-500 ml-1">(Raw)</span>
                    )}
                  </div>
                  <div className="text-lg text-gray-500">
                    {total >= 70 ? '★★★' : total >= 40 ? '★★' : '★'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Sticky Mini Header */}
      {isSticky && (
        <div className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-md z-40 transition-all duration-300">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-bold text-[#18365E] truncate max-w-md">{title}</h2>
                <UIBadge className="bg-[#2C5282] text-white px-2 py-1 rounded text-xs">{category}</UIBadge>
              </div>
              <div className="flex items-center gap-6">
                <div className="bg-green-50 px-3 py-2 rounded-lg">
                  <div className="text-lg font-bold text-green-600">{roiRange}</div>
                  <div className="text-xs text-gray-600">
                    {timeToRoi === 'No payback' ? (
                      <span className="text-red-600">No payback</span>
                    ) : (
                      <>ROI in {timeToRoi}</>
                    )}
                  </div>
                </div>
                <UIBadge className="bg-[#D69E2E] text-white px-2 py-1 rounded text-xs font-bold">
                  Rank #{globalRank}
                </UIBadge>
                <div className="flex items-center gap-3">
                  <div className="text-lg font-bold text-gray-700">
                    {Math.round(total)}/100
                  </div>
                  <div className="text-sm text-gray-500">
                    {total >= 70 ? '★★★' : total >= 40 ? '★★' : '★'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}